import type { RefObject } from 'react'

import { Fragment, useRef } from 'react'
import { cn } from '@/lib/utils'

import {
    gsap,
    MOTION,
    EASE,
    registerGsap,
    prefersReducedMotion,
    useIsomorphicLayoutEffect
} from '@/lib/motion'

export type HeadingSegment = {
    text: string
    /** Renders the segment in the italic display serif, as the original does. */
    serif?: boolean
}

type HeadingTag = 'h1' | 'h2' | 'h3' | 'p'

type AnimatedHeadingProps = {
    segments: HeadingSegment[]
    as?: HeadingTag
    className?: string
    /** Play on mount instead of waiting for the heading to scroll into view. */
    immediate?: boolean
    delay?: number
    stagger?: number
    start?: string
}

const toWords = (value: string): string[] => value.split(' ').filter(Boolean)

/**
 * The site's signature headline treatment: every word is its own inline-block,
 * so they rise, fade and un-blur one after another exactly the way the original
 * Framer text-appear effect does.
 */
export const AnimatedHeading = ({
    segments,
    as: Tag = 'h2',
    className,
    immediate = false,
    delay = 0,
    stagger = MOTION.stagger,
    start = MOTION.start
}: AnimatedHeadingProps) => {
    const ref = useRef<HTMLHeadingElement | null>(null)

    useIsomorphicLayoutEffect(() => {
        const node = ref.current

        if (!node || prefersReducedMotion()) {
            return
        }

        registerGsap()

        const context = gsap.context(() => {
            const words = node.querySelectorAll('[data-word]')

            if (!words.length) {
                return
            }

            gsap.set(words, {
                opacity: 0,
                yPercent: 60,
                filter: `blur(${MOTION.blur}px)`,
                willChange: 'transform, opacity, filter'
            })

            gsap.to(words, {
                opacity: 1,
                yPercent: 0,
                filter: 'blur(0px)',
                duration: MOTION.duration,
                delay,
                ease: EASE.enter,
                stagger,
                clearProps: 'filter,willChange',
                scrollTrigger: immediate
                    ? undefined
                    : {
                        trigger: node,
                        start,
                        once: true
                    }
            })
        }, node)

        return () => context.revert()
    }, [immediate, delay, stagger, start])

    let counter = 0

    return (
        <Tag
            ref={ref as RefObject<HTMLHeadingElement>}
            className={cn('[&_[data-word]]:inline-block', className)}
        >
            {segments.map((segment, segmentIndex) => {
                const words = toWords(segment.text)

                return (
                    <Fragment key={`${segment.text}-${segmentIndex}`}>
                        {words.map((word, wordIndex) => {
                            counter += 1

                            return (
                                <Fragment key={`${word}-${counter}`}>
                                    <span
                                        data-word=''
                                        className={cn(segment.serif && 'serif-accent')}
                                    >
                                        {word}
                                    </span>

                                    {wordIndex < words.length - 1 ? ' ' : null}
                                </Fragment>
                            )
                        })}

                        {segmentIndex < segments.length - 1 ? ' ' : null}
                    </Fragment>
                )
            })}
        </Tag>
    )
}
